import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';

import { Sweetener, SweetenerService } from 'us-web-services';

import { Checkbox } from 'us-react-components/dist/Checkbox/Checkbox';
import { LogoSpinner } from 'us-react-components/dist/LogoSpinner/LogoSpinner';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Routes } from '../../../Routes';
import { useDependencies } from '../../../dependencies';

const NONE_OF_THE_ABOVE = 'none';

export const Sweeteners = observer((): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [noneOfTheAbove, setNoneOfTheAbove] = useState(false);
  const [sweetenersAvailable, setSweetenersAvailable] = useState<Sweetener[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores: { familyRegistration }, services: { tracker } } = useDependencies();

  familyRegistration.setCurrentRoute(Routes.parent.registration.sweeteners);
  useEffect(() => {
    const fetchSweeteners = async (): Promise<void> => {
      try {
        const sweetenersResponse = (await SweetenerService.getBookingSweeteners()).data;
        setSweetenersAvailable(sweetenersResponse.data);
      } catch (error) {
        console.error('Error fetching sweeteners', error);
      } finally {
        setLoading(false);
      }
    }

    fetchSweeteners();
  }, []);

  const handleContinue = (): void => {
    navigate(Routes.parent.registration.details)
  }

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const sweeteners = familyRegistration.sweeteners ?? [];

    let updatedSweeteners: Sweetener[] = [...sweeteners];

    if (e.target.id === `sweetener-${NONE_OF_THE_ABOVE}`) {
      updatedSweeteners = [];
      setNoneOfTheAbove(!noneOfTheAbove);
    } else {
      const sweetenerId = Number.parseInt(e.target.id.substr(10), 10);
      const selectedSweetener = sweetenersAvailable.find(s => s.id === sweetenerId);

      if (sweeteners.some(s => s.id === sweetenerId)) {
        updatedSweeteners = sweeteners.filter(s => s.id !== sweetenerId);
      } else if (selectedSweetener) {
        updatedSweeteners.push(selectedSweetener);
      }
      setNoneOfTheAbove(false);
    }

    familyRegistration.setSweeteners(updatedSweeteners);
  }

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={t('pages.nux.family.sweeteners.title')}
      onContinue={handleContinue}>
      <div className='flex w-[80%] flex-col py-8'>
        {loading ? <LogoSpinner /> :
          <>
            {sweetenersAvailable.map((sweetener: Sweetener) => (
              <Checkbox
                checked={familyRegistration.sweeteners?.some(s => s.id === sweetener.id) ?? false}
                className='pb-2'
                id={`sweetener-${sweetener.id}`}
                key={sweetener.id}
                label={sweetener.label ?? undefined}
                onChange={handleCheckboxChange}
              />
            ))}
            <Checkbox
              checked={noneOfTheAbove}
              id={`sweetener-${NONE_OF_THE_ABOVE}`}
              onChange={handleCheckboxChange}
              label={t('pages.nux.family.sweeteners.noneOfTheAbove')}
            />
          </>}
      </div>
    </NuxContainer>
  )
});
